import cx from 'classnames';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import type { ReactZoomPanPinchProps } from 'react-zoom-pan-pinch';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { IconButton } from '@zen/DesignSystem';
import type { IconName } from '@zen/Styleguide';

interface ZoomEvents {
  resetTransform: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
}

interface Props {
  additionalControlElement?: ReactNode;
  children: ReactNode;
  zoomProps?: ReactZoomPanPinchProps;
}

const ZoomElement: FC<Props> = ({ additionalControlElement, children, zoomProps }) => {
  const [isGrabbing, setIsGrabbing] = useState(false);
  const setGrabbingCursor = (): void => setIsGrabbing(true);
  const unsetGrabbingCursor = (): void => setIsGrabbing(false);

  const renderZoomControlButton = (action: () => void, icon: IconName, title: string): ReactNode => {
    return <IconButton icon={icon} onClick={() => action()} title={title} variant="secondary" />;
  };

  const panelClassName: string = cx('relative flex pt-10', { 'cursor-grabbing': isGrabbing, 'cursor-grab': !isGrabbing });

  return (
    <TransformWrapper initialScale={1} wheel={{ disabled: false }} {...zoomProps}>
      {({ zoomIn, zoomOut, resetTransform }: ZoomEvents): ReactNode => (
        <div
          className={panelClassName}
          onMouseDown={setGrabbingCursor}
          onMouseLeave={unsetGrabbingCursor}
          onMouseUp={unsetGrabbingCursor}
        >
          <div className="absolute right-0 -top-4 z-10" data-testid="zoom-control-buttons">
            <div className="flex p-3 space-x-2 rounded-sm cursor-default">
              {renderZoomControlButton(zoomIn, 'zicon-add', 'Zoom in')}
              {renderZoomControlButton(zoomOut, 'zicon-minus', 'Zoom out')}
              {renderZoomControlButton(resetTransform, 'zicon-updown', 'Zoom reset')}
              {additionalControlElement && additionalControlElement}
            </div>
          </div>
          <TransformComponent>{children}</TransformComponent>
        </div>
      )}
    </TransformWrapper>
  );
};

export default ZoomElement;
